import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {trackDescriptionTabViewIfValidSequence} from 'molbio/SequenceAnalyticsHelper';
import {EditorPropertiesContext, NoteContext} from 'notebook/notes/core-ui/NoteContext';
import GeneralNoteView from 'notebook/notes/tabs/GeneralNoteView';
import NoteTabState from 'notebook/notes/tabs/NoteTabState';
import {getDedicatedGeneralNoteViewProps, getSharedNoteViewProps} from 'notebook/notes/tabs/NoteViewProps';
import {EntryNoteView} from 'notebook/notes/tabs/NoteViews';
import PublishingSubtemplateNoteView from 'notebook/notes/tabs/PublishingSubtemplateNoteView';
import PublishingTemplateNoteView from 'notebook/notes/tabs/PublishingTemplateNoteView';
import SubtemplateNoteView, {SUBTEMPLATE_VIEW_PRINT_OPTIONS} from 'notebook/notes/tabs/SubtemplateNoteView';
import {
  TemplateNoteView,
  TEMPLATE_VIEW_PRINT_OPTIONS,
  TEMPLATE_VIEW_PRINT_OPTIONS_WITHOUT_PROTOCOLS,
} from 'notebook/notes/tabs/TemplateNoteView';
import isConfiguration from 'notebook/util/isConfiguration';
import * as Perf from 'performance/Perf';
import {PerfId} from 'performance/Perf';
import * as ReduxDOMRender from 'redux-stores/ReduxDOMRender';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {hasAnyNavigationHash} from 'ui-library/hooks/useHashNavigation';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';
import StageEntryNoteView from 'workflows/stage-entries/StageEntryNoteView';

function __guard__<T, V>(value: Optional<T>, transform: (arg: T) => V): V | undefined {
  return value != null ? transform(value) : undefined;
}

const SCROLLING_PERF_INTERVAL = 1 * 60 * 1000;

export default class NoteTabView extends Backbone.View<NoteTabState> {
  scrollingPerf?: PerfId;

  static initClass(): void {
    this.prototype.className = 'buffer-tab buffer-tab--no-padding';
  }

  override initialize(): void {
    // Bind method to `this` so it can be threaded into React components
    this.noteOnLoad = this.noteOnLoad.bind(this);
    this._getScrollingPerfMetadata = this._getScrollingPerfMetadata.bind(this);
    trackDescriptionTabViewIfValidSequence(this.model.get('model'));
  }

  override render(): this {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('model'), 'change', this.rerender);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('displayOptions'), 'redraw', this.rerender);
    this.rerender();
    return this;
  }

  noteOnLoad(): void {
    const scrollingPerf = Perf.start(
      `${APIUtil.apiIdToType(this.model.get('fileId'))}_note_view_scroll`,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      {enabled: true, getMetadata: this._getScrollingPerfMetadata},
      {
        interval: SCROLLING_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
      }
    );
    this.scrollingPerf = scrollingPerf;
    Perf.markStart(scrollingPerf);
    // Keep scroll top value between rerenders.
    const scroller = this.$('.noteEditor-scrollContainer');
    scroller.on('scroll', () => this.model.set({scrollTop: scroller.scrollTop()}, {silent: true}));
    // We have to defer this, because we need to wait till the tab is added
    // to the DOM. Setting scroll top before the DOM node is in the document
    // doesn't work.
    _.defer(() => {
      // Only restore scroll position if there's no hash navigation happening
      if (!hasAnyNavigationHash()) {
        scroller.scrollTop(this.model.get('scrollTop'));
      }
      Perf.markEnd(scrollingPerf);
    });
  }

  afterAttach(): void {
    this.rerender();
  }

  getRootFolderId(): string | null {
    const {attributes} = this.model.get('model');
    return attributes?.entry?.folder?.source?.id ?? null;
  }

  getTemplateCollectionId(): string | null {
    const {attributes} = this.model.get('model');
    return attributes.template?.templateCollection?.id ?? null;
  }

  _getScrollingPerfMetadata(): {entryId?: string} {
    const {attributes} = this.model.get('model');
    if (attributes && attributes.entry) {
      return {
        entryId: attributes.entry.entryId,
      };
    }
    return {};
  }

  rerender(): void {
    const noteProps = {
      ...getSharedNoteViewProps(this.model),
      // eslint-disable-next-line @typescript-eslint/unbound-method
      onLoad: this.noteOnLoad,
    };
    const showFormulas = this.model.get('displayOptions').getOption(vo.ELN_SHOW_FORMULAS.name).get('visible');

    const highlightCustomColumns = this.model
      .get('displayOptions')
      .getOption(vo.ELN_HIGHLIGHT_CUSTOM_COLUMNS.name)
      .get('visible');
    const banner = this.model.get('model').getBannerText();
    // eslint-disable-next-line no-restricted-syntax
    _.extend(
      noteProps,
      (() => {
        switch (this.model.get('model').fileType()) {
          case ModelTypeConstants.ENTRY:
            return {
              banner,
              note: __guard__(this.model.get('model').proxied(), (entry) => entry.note),
              structuredTableMappings: __guard__(
                this.model.get('model').proxied(),
                (entry) => entry.structuredTableMappings
              ),
              dirty: this.model.get('model').isDirty(),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (entry) => entry.name),
              parentType: 'Entry',
              review: this.model.get('model').review(),
              printOptions:
                this.model.get('model').get('entry').entryProtocols.length > 0
                  ? EntryNoteView.PRINT_OPTIONS
                  : EntryNoteView.PRINT_OPTIONS_WITHOUT_PROTOCOLS,
              collaboratorIds: this.model.get('model').collaboratorIds(),
              templateVersionId: this.model.get('model').templateVersionId(),
              templateUrl: this.model.get('model').templateUrl(),
              templateVersionTimestamp: this.model.get('model').templateVersionTimestamp(),
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
              authors: this.model.get('model').authors(),
              focusedTableProvenanceUuid: this.model.get('model').get('entry').focusedTableProvenanceUuid,
              focusedTableViewMode: this.model.get('model').get('entry').focusedTableViewMode,
            };
          case ModelTypeConstants.STAGE_ENTRY:
            return {
              banner,
              note: __guard__(
                this.model.get('model').proxied(),
                (stageEntryDoc) => stageEntryDoc.entryVersion.note
              ),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (stageEntryDoc) => stageEntryDoc.name),
              review: this.model.get('model').review(),
              parentType: 'Workflow Entry',
              printOptions:
                this.model.get('model').get('stageEntryDocument').entryVersion.entryProtocols.length > 0
                  ? EntryNoteView.PRINT_OPTIONS
                  : EntryNoteView.PRINT_OPTIONS_WITHOUT_PROTOCOLS,
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
            };
          case ModelTypeConstants.TEMPLATE:
            return {
              banner,
              isUnsaved: this.model.get('model').isUnsaved(),
              note: __guard__(
                this.model.get('model').proxied(),
                (template) => template.templateVersion.entryVersion.note
              ),
              structuredTableMappings: __guard__(
                this.model.get('model').proxied(),
                (template) => template.templateVersion.entryVersion.structuredTableMappings
              ),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (template) => template.name),
              parentType: 'Template',
              printOptions:
                this.model.get('model').get('template').templateVersion.entryVersion.entryProtocols.length > 0
                  ? TEMPLATE_VIEW_PRINT_OPTIONS
                  : TEMPLATE_VIEW_PRINT_OPTIONS_WITHOUT_PROTOCOLS,
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
              focusedTableProvenanceUuid: this.model.get('model').get('template').focusedTableProvenanceUuid,
              focusedTableViewMode: this.model.get('model').get('template').focusedTableViewMode,
              openVersionApiId: this.model.get('model').openVersionApiId(),
              openVersionModifiedAt: this.model.get('model').openVersionModifiedAt(),
            };
          case ModelTypeConstants.SUBTEMPLATE:
            return {
              banner,
              note: __guard__(
                this.model.get('model').proxied(),
                (subtemplate) => subtemplate.subtemplateVersion.noteVersion
              ),
              isLatest: this.model.get('model').isLatest(),
              title: __guard__(this.model.get('model').proxied(), (subtemplate) => subtemplate.name),
              parentType: 'Sub-Template',
              printOptions: SUBTEMPLATE_VIEW_PRINT_OPTIONS,
              lockedFromExternalChange: this.model.get('model').lockedFromExternalChange(),
              openVersionApiId: this.model.get('model').openVersionApiId(),
              openVersionModifiedAt: this.model.get('model').openVersionModifiedAt(),
            };
          default:
            return {...getDedicatedGeneralNoteViewProps(this.model)};
        }
      })()
    );

    const NoteViewCls = (() => {
      switch (this.model.get('model').fileType()) {
        case ModelTypeConstants.ENTRY:
          return EntryNoteView;
        case ModelTypeConstants.STAGE_ENTRY:
          return StageEntryNoteView;
        case ModelTypeConstants.TEMPLATE:
          if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING) {
            return PublishingTemplateNoteView;
          }
          return TemplateNoteView;
        case ModelTypeConstants.SUBTEMPLATE:
          if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING) {
            return PublishingSubtemplateNoteView;
          }
          return SubtemplateNoteView;
        default:
          return GeneralNoteView;
      }
    })();
    ReduxDOMRender.renderToDOM(
      // @ts-expect-error noteProps.note will always be undefined, because getSharedNoteViewProps does not return note
      <NoteContext.Provider value={{note: noteProps.note}}>
        <EditorPropertiesContext.Provider
          value={{
            showFormulas,
            highlightCustomColumns,
            rootFolderId: this.getRootFolderId(),
            fileId: noteProps.fileId,
            templateCollectionId: this.getTemplateCollectionId(),
            isConfig: isConfiguration(noteProps.fileId),
          }}
        >
          {/* @ts-expect-error No common signature between these note view classes */}
          <NoteViewCls {...noteProps} />
        </EditorPropertiesContext.Provider>
      </NoteContext.Provider>,
      this.el
    );
  }

  /**
   * `Backbone.View.remove()` without cleaning up nested $.data usage.
   *
   * `Backbone.View.remove()` calls `this.$el.remove()` which calls `$.cleanData`. If this is called before
   * `$.tooltip('destroy')`, the tooltips will be stuck on the page. For robustness against cases where
   * the cleanup of children nodes is called after this, we avoid calling `$.cleanData()` altogether.
   *
   * Reference:
   *   - https://github.com/jashkenas/backbone/blob/1.1.2/backbone.js#L1034-L1040
   *   - https://github.com/benchling/aurelia/blob/fcdd6d9/packages/%40benchling-jquery/src/jquery.js#L5305-L5324
   *   - https://api.jquery.com/remove/
   *
   * TODO(BNCH-48627): Remove this hack once we migrate away from Bootstrap tooltips.
   */
  _removeWithoutCleanData(): void {
    this.$el.detach();
    this.stopListening();
  }

  override remove(): this {
    Perf.stop(this.scrollingPerf!);
    ReactDOM.unmountComponentAtNode(this.el);
    // If NoteTabView component is rendered within a React component, then the above unmount will be async,
    // so we defensively use `_removeWithoutCleanData` in place of `super.remove()`.
    this._removeWithoutCleanData();
    return this;
  }
}
NoteTabView.initClass();
